import axios from "axios";
import appConfig from "../config/appConfig";

// Create an axios instance with base URL and timeout.
const authApiClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/auth`,
  timeout: appConfig.timeoutDuration,
  headers: { "Content-Type": "application/json" }
});

// Attach the token to API requests if available.
authApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(appConfig.auth.tokenStorageKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


const authApi = {
  /**
   * User Login
   * @param {Object} credentials - { username, password }
   * @returns {Promise<Object>} Authenticated user data and token
   */
  login: async (credentials) => {
    try {
      const response = await authApiClient.post("/login", credentials);
      const { access_token, refresh_token } = response.data;

      if (access_token) {
          localStorage.setItem(appConfig.auth.tokenStorageKey, access_token);
      }
      if (refresh_token) {
        localStorage.setItem(appConfig.auth.refreshTokenStorageKey, refresh_token);
      }
      // authApiClient.defaults.headers.Authorization = `Bearer ${access_token}`;

      return response.data;
    } catch (error) {
      console.error("Login API error:", error);
      throw new Error(error.response?.data?.detail || "Login failed");
    }
  },

  /**
   * User Registration
   * @param {Object} userData - { username, email, password }
   * @returns {Promise<Object>} Registered user data
   */
  register: async (userData) => {
    try {
      const response = await authApiClient.post("/register", userData);
      return response.data;
    } catch (error) {
      console.error("Registration API error:", error);
      throw new Error(error.response?.data?.detail || "Registration failed");
    }
  },

  /**
   * Get Authenticated User Profile
   * @returns {Promise<Object>} User profile data
   */
  getProfile: async () => {
    try {
      const response = await authApiClient.get("/me");
      return response.data;
    } catch (error) {
      console.error("GetProfile API error:", error);
      throw new Error(error.response?.data?.detail || "Failed to fetch profile");
    }
  },

  /**
   * Update Authenticated User Profile
   * @param {Object} profileData - { email?, password? }
   * @returns {Promise<Object>} Updated user profile data
   */
  updateProfile: async (profileData) => {
    try {
      const response = await authApiClient.put("/me", profileData);
      return response.data;
    } catch (error) {
      console.error("UpdateProfile API error:", error);
      throw new Error(error.response?.data?.detail || "Failed to update profile");
    }
  },

  googleLogin: async (idToken) => {
    try {
      const response = await authApiClient.post('/google', { id_token: idToken });
      const { access_token, refresh_token } = response.data;
      if (access_token) localStorage.setItem(appConfig.auth.tokenStorageKey, access_token);
      if (refresh_token) localStorage.setItem(appConfig.auth.refreshTokenStorageKey, refresh_token);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Google sign-in failed');
    }
  },

  refresh: async (refreshToken) => {
    const response = await authApiClient.post('/refresh', { refresh_token: refreshToken });
    return response.data;
  },

  logout: async (refreshToken) => {
    await authApiClient.post('/logout', { refresh_token: refreshToken });
  },

  logoutAll: async () => {
    await authApiClient.post('/logout-all');
  }
};

export default authApi;
